import React, { useState, useEffect } from 'react';
import { staffAPI } from '../services/api';
import { toast } from 'sonner';
import { StaffSearchPanel } from './guard/StaffSearchPanel';
import { ActiveStaffPanel } from './guard/ActiveStaffPanel';

export default function StaffCheckIn() {
  const [idNumber, setIdNumber] = useState('');
  const [villaSearch, setVillaSearch] = useState('');
  const [searchedStaff, setSearchedStaff] = useState(null);
  const [villaStaffList, setVillaStaffList] = useState([]);
  const [selectedVilla, setSelectedVilla] = useState('');
  const [activeStaff, setActiveStaff] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchMode, setSearchMode] = useState('id');

  const loadActiveStaff = async () => {
    try {
      const { data } = await staffAPI.getActive();
      setActiveStaff(data.active_staff || []);
    } catch (error) {
      console.error('Failed to load active staff:', error);
    }
  };

  useEffect(() => {
    loadActiveStaff();
    const interval = setInterval(loadActiveStaff, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSearchById = async () => {
    if (!idNumber) { toast.error('Please enter ID number'); return; }
    setSearching(true);
    setVillaStaffList([]);
    try {
      const { data } = await staffAPI.search(idNumber);
      setSearchedStaff(data);
      setSelectedVilla(data.assigned_villas[0] || '');
    } catch {
      toast.error('Staff not found');
      setSearchedStaff(null);
    } finally { setSearching(false); }
  };

  const handleSearchByVilla = async () => {
    if (!villaSearch) { toast.error('Please enter villa number'); return; }
    setSearching(true);
    setSearchedStaff(null);
    try {
      const { data } = await staffAPI.getByVilla(villaSearch.toUpperCase());
      if (data?.length > 0) {
        setVillaStaffList(data);
        toast.success(`Found ${data.length} staff assigned to Villa ${villaSearch}`);
      } else {
        setVillaStaffList([]);
        toast.warning(`No staff found for Villa ${villaSearch}`);
      }
    } catch {
      toast.error('Failed to search staff by villa');
      setVillaStaffList([]);
    } finally { setSearching(false); }
  };

  const handleSelectStaffFromVilla = (staff) => {
    setSearchedStaff(staff);
    setSelectedVilla(villaSearch.toUpperCase());
  };

  const handleCheckIn = async () => {
    if (!selectedVilla) { toast.error('Please select villa'); return; }
    try {
      const { data } = await staffAPI.checkIn({ staff_id: searchedStaff.id, villa_number: selectedVilla });
      toast.success('Staff checked in! Entry Pass: ' + data.entry.entry_pass_number);
      clearSearch();
      loadActiveStaff();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOut = async (entryId) => {
    try {
      await staffAPI.checkOut(entryId);
      toast.success('Staff checked out successfully');
      loadActiveStaff();
    } catch {
      toast.error('Check-out failed');
    }
  };

  const clearSearch = () => {
    setSearchedStaff(null);
    setVillaStaffList([]);
    setIdNumber('');
    setVillaSearch('');
    setSelectedVilla('');
  };

  return (
    <div className="space-y-6">
      <StaffSearchPanel
        searchMode={searchMode} setSearchMode={setSearchMode}
        idNumber={idNumber} setIdNumber={setIdNumber} handleSearchById={handleSearchById}
        villaSearch={villaSearch} setVillaSearch={setVillaSearch} handleSearchByVilla={handleSearchByVilla}
        villaStaffList={villaStaffList} handleSelectStaffFromVilla={handleSelectStaffFromVilla}
        searchedStaff={searchedStaff} selectedVilla={selectedVilla} setSelectedVilla={setSelectedVilla}
        handleCheckIn={handleCheckIn} clearSearch={clearSearch} searching={searching}
      />
      <ActiveStaffPanel activeStaff={activeStaff} handleCheckOut={handleCheckOut} />
    </div>
  );
}
